import * as React from 'react';
import { Alert, Spinner } from 'react-bootstrap';
import { cloneEntity, Entity, isBlank, Row } from 'gossamer-unified';
import { useCacheRecord } from './useCacheRecord';
import { isNotFound } from './ReduxGCacheMiddleware';

export interface SubSectionSingleProps<T extends Row> {
  entity: Entity<T>;
  fieldGroup?: string;
  keyValue: string;
  row: T;
}

export type RenderAllProps<T extends Row> = SubSectionSingleProps<T> & {
  error: any;
  isFetching: boolean;
  isNotFound: boolean;
  row?: T;
};

export interface RenderErrorProps {
  error: any;
}

const DefaultError = (props: RenderErrorProps): React.JSX.Element => <Alert variant="danger">{props.error}</Alert>;

export interface RenderNoLoadOrNotFoundProps {
  keyValue: string;
}

const DefaultFetching = () => <Spinner animation="border" size="sm" />;

const DefaultNoLoad = (props: RenderNoLoadOrNotFoundProps) => <div>{props.keyValue}</div>;

const DefaultNotFound = (props: RenderNoLoadOrNotFoundProps) => (
  <Alert variant="danger">Not found: {props.keyValue}</Alert>
);

interface Props<T extends Row> {
  alterEntity?: (cloneEntity: Entity<T>) => void;
  entity: Entity<T>;
  fieldGroup?: string;
  keyValue: string;
  loadCondition?: (keyValue: string) => boolean;
  render?: React.FC<SubSectionSingleProps<T>>;
  renderAll?: React.FC<RenderAllProps<T>>;
  renderBlank?: React.FC;
  renderError?: React.FC<RenderErrorProps>;
  renderFetching?: React.FC;
  renderNoLoad?: React.FC<RenderNoLoadOrNotFoundProps>;
  renderNotFound?: React.FC<RenderNoLoadOrNotFoundProps>;
}

export const QuerySingle = <T extends Row>(props: Props<T>): React.JSX.Element => {
  const localEntity = React.useMemo(() => {
    if (props.alterEntity) {
      const localEntity = cloneEntity(props.entity);
      props.alterEntity(localEntity);
      return localEntity;
    }
    return props.entity;
  }, [props.entity, props.alterEntity]);

  if (isBlank(props.keyValue) && !props.renderAll) {
    const Render = props.renderBlank || (() => null);
    return <Render />;
  }
  if (props.loadCondition && !props.loadCondition(props.keyValue) && !props.renderAll) {
    const Render = props.renderNoLoad || DefaultNoLoad;
    return <Render keyValue={props.keyValue} key={props.keyValue} />;
  }
  return <QuerySingleGetData {...props} entity={localEntity} key={props.keyValue} />;
};

const QuerySingleGetData = <T extends Row>(props: Props<T>) => {
  const record = useCacheRecord(props.entity, props.keyValue);
  return (
    <QuerySingleInner
      {...props}
      row={record.data as T | undefined}
      error={record.error}
      isFetching={record.isFetching}
      isNotFound={isNotFound(record)}
    />
  );
};

type QuerySingleInnerProps<T extends Row> = Props<T> & {
  row?: T;
  error: any;
  isFetching: boolean;
  isNotFound: boolean;
};

const QuerySingleInner = <T extends Row>(props: QuerySingleInnerProps<T>): React.JSX.Element => {
  if (props.renderAll) {
    const SubSection = props.renderAll;
    return <SubSection {...(props as RenderAllProps<T>)} />;
  }
  if (props.row) {
    if (!props.render) {
      throw new Error(`either render or renderAll must be supplied as a prop`);
    }
    const SubSection = props.render;
    return <SubSection entity={props.entity} fieldGroup={props.fieldGroup} keyValue={props.keyValue} row={props.row} />;
  }
  if (props.isNotFound) {
    const RenderNotFound = props.renderNotFound || DefaultNotFound;
    return <RenderNotFound keyValue={props.keyValue} />;
  }
  const RenderError = props.renderError || DefaultError;
  if (props.error) {
    return <RenderError error={props.error} />;
  }
  if (props.isFetching) {
    const RenderLoading = props.renderFetching || DefaultFetching;
    return <RenderLoading />;
  }
  return <></>;
};
